import { useState } from "react";
import { IconQuestionMark } from "@tabler/icons-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  IconAction,
  Input,
  Stack,
  Text,
  Textarea,
} from "@/components/ui";
import { PERMISOS } from "@/lib/permisos";
import { useUserPermisos } from "@/lib/auth-store";
import { useCrearSolicitudCatalogo, useTiposSolicitudCatalogoActivos } from "../hooks/use-solicitud-catalogo";

type TipoPorOrganizacion = "CentroDeCosto" | "CuentaContable";

const ETIQUETA: Record<TipoPorOrganizacion, { elemento: string; codigo: string; ejemplo: string }> = {
  CentroDeCosto: { elemento: "centro de costos", codigo: "Código del centro de costos", ejemplo: "p. ej. 9002300001" },
  CuentaContable: { elemento: "cuenta contable", codigo: "Código de la cuenta contable", ejemplo: "p. ej. 6201010001" },
};

/**
 * «¿No está? Solicitarlo» junto a los selectores de centro de costos / cuenta contable del
 * prorrateo (D-2 del módulo Solicitudes de catálogo): el elemento no existe o no está asignado
 * a la organización de la solicitud. El solicitante escribe el código y se pide su alta (o su
 * asignación) PARA esa organización. Se oculta si el tipo está inactivo, si el usuario no puede
 * solicitar o si la solicitud aún no tiene organización.
 */
export function SolicitarElementoNoListado({
  tipo,
  numeroSociedad,
  solicitudPagoId,
  numeroSolicitudPago,
}: {
  tipo: TipoPorOrganizacion;
  numeroSociedad?: string;
  solicitudPagoId?: string;
  numeroSolicitudPago?: number;
}) {
  const permisos = useUserPermisos();
  const { data: tipos } = useTiposSolicitudCatalogoActivos();
  const crear = useCrearSolicitudCatalogo();
  const [abierto, setAbierto] = useState(false);
  const [clave, setClave] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [comentario, setComentario] = useState("");

  const config = tipos?.find((t) => t.tipo === tipo);
  if (!numeroSociedad || !config || !permisos.includes(PERMISOS.catalogosSolicitar)) return null;

  const copy = ETIQUETA[tipo];
  const cerrar = () => {
    setAbierto(false);
    setClave("");
    setDescripcion("");
    setComentario("");
  };
  const enviar = () =>
    crear.mutate(
      {
        tipo,
        clave: clave.trim(),
        numeroSociedad,
        descripcion: descripcion.trim() || undefined,
        comentario: comentario.trim() || undefined,
        solicitudPagoId,
        numeroSolicitudPago,
      },
      { onSuccess: cerrar }
    );

  return (
    <>
      <IconAction label={`¿No está el ${copy.elemento}? Solicitarlo`} size="sm" onClick={() => setAbierto(true)}>
        <IconQuestionMark size={14} stroke={1.8} />
      </IconAction>
      <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{config.textoBoton}</DialogTitle>
          </DialogHeader>
          <Stack gap="md">
            <Text size="sm">
              ¿No encuentras el {copy.elemento}? Puede no existir o no estar asignado a tu organización (sociedad{" "}
              <strong>{numeroSociedad}</strong>). Escribe su código y se pedirá su alta a los responsables.
            </Text>
            <Input
              label={copy.codigo}
              placeholder={copy.ejemplo}
              value={clave}
              onChange={(e) => setClave(e.currentTarget.value)}
              maxLength={60}
              className="font-mono"
              required
            />
            <Input
              label="Nombre o descripción"
              placeholder="Opcional"
              value={descripcion}
              onChange={(e) => setDescripcion(e.currentTarget.value)}
              maxLength={300}
            />
            <Textarea
              label="Comentario para los responsables"
              placeholder="Opcional · contexto de por qué lo necesitas"
              value={comentario}
              onChange={(e) => setComentario(e.currentTarget.value)}
              maxLength={500}
              rows={2}
            />
          </Stack>
          <DialogFooter>
            <Button variant="default" onClick={cerrar}>
              Cancelar
            </Button>
            <Button disabled={!clave.trim()} loading={crear.isPending} onClick={enviar}>
              Enviar solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
